import { useEffect, useState, ReactNode } from "react";
import { CrossDomainTrackingContext } from "../context";
import { CrossDomainParams } from "../types";

interface CrossDomainTrackingSourceProviderProps {
  children: ReactNode;
  tagId: string;
}

/**
 * Get GA4 client ID and session ID for cross-domain tracking
 */
const getGA4CrossDomainParams = (tagId: string): Promise<CrossDomainParams> => {
  return new Promise((resolve) => {
    const params: CrossDomainParams = {};
    let completed = 0;
    const total = 2;

    const checkComplete = () => {
      completed++;
      if (completed === total) {
        resolve(params);
      }
    };

    // Get client ID
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag("get", tagId, "client_id", (clientId: string) => {
        params.clientId = clientId;
        checkComplete();
      });
    } else {
      checkComplete();
    }

    // Get session ID
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag("get", tagId, "session_id", (sessionId: string) => {
        params.sessionId = sessionId;
        checkComplete();
      });
    } else {
      checkComplete();
    }
  });
};

export const CrossDomainTrackingSourceProvider = ({
  children,
  tagId,
}: CrossDomainTrackingSourceProviderProps) => {
  const [crossDomainParams, setCrossDomainParams] = useState<CrossDomainParams>(
    {},
  );
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeParams = async () => {
      try {
        // Wait for GA4 to be available
        if (typeof window !== 'undefined' && window.gtag) {
          const params = await getGA4CrossDomainParams(tagId);
          setCrossDomainParams(params);
        } else {
          // If GA4 is not available yet, wait a bit and try again
          setTimeout(async () => {
            if (typeof window !== 'undefined' && window.gtag) {
              const params = await getGA4CrossDomainParams(tagId);
              setCrossDomainParams(params);
            }
            setIsInitialized(true);
          }, 1000);
          return;
        }
      } catch (error) {
        console.error("Error getting GA4 cross-domain parameters:", error);
      }

      setIsInitialized(true);
    };

    initializeParams();
  }, [tagId]);

  return (
    <CrossDomainTrackingContext.Provider
      value={{ crossDomainParams, isInitialized }}
    >
      {children}
    </CrossDomainTrackingContext.Provider>
  );
};
